import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  arrayRemove,
  getFirestore,
} from "firebase/firestore";
import { useUser } from "../shared/UserContext";
import { gradeFields, studentGrade } from "../shared/gradeModel";
import MobileTableCards from "../shared/MobileTableCards";
import { byLastName } from "../shared/nameSort";

const DisplayEnrolledStudentsTable = ({ selectedClass, onStudentsUpdated }) => {
  const { schoolId, uid, role } = useUser();
  const [students, setStudents] = useState([]);
  const [editingStudent, setEditingStudent] = useState(null);
  const db = getFirestore();

  useEffect(() => {
    const fetchEnrolledStudents = async () => {
      if (!selectedClass || !schoolId) {
        setStudents([]);
        return;
      }

      try {
        // Load the roster the same way Attendance does — students whose own
        // enrolledClasses contains this class. This is reliable even when the
        // class doc's enrolledStudents array is stale/empty (the cause of 5MC
        // showing no students here while Attendance found them). schoolId scopes
        // the query so the security rules authorize it.
        const snap = await getDocs(
          query(
            collection(db, "students"),
            where("schoolId", "==", schoolId),
            where("enrolledClasses", "array-contains", selectedClass)
          )
        );
        const sortedStudents = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(Boolean)
          .sort(byLastName);
        setStudents(sortedStudents);
      } catch (error) {
        console.error("Error fetching enrolled students:", error);
        setStudents([]);
      }
    };

    fetchEnrolledStudents();
  }, [db, selectedClass, schoolId, onStudentsUpdated]);

  // Remove the student from THIS class only — they stay in the school directory
  // and any other classes (students are the source of truth, the same pattern
  // School Roster / class-delete use). This is NOT a hard delete of the doc.
  const handleRemove = async (student) => {
    const name = `${student.firstName || ""} ${student.lastName || ""}`.trim() || "this student";
    if (!window.confirm(
      `Remove ${name} from this class? They stay in the directory and any other classes — this only takes them off this class's roster.`
    )) return;

    try {
      const studentDocRef = doc(db, "students", student.id);
      const studentUpdate = { enrolledClasses: arrayRemove(selectedClass) };
      if (student.homeroomClassId === selectedClass) studentUpdate.homeroomClassId = null;
      await updateDoc(studentDocRef, studentUpdate);

      // Keep the class doc's own enrolledStudents array reconciled too.
      const classDocRef = doc(db, "classes", selectedClass);
      const classDoc = await getDoc(classDocRef);
      const classData = classDoc.data() || {};
      if ((classData.enrolledStudents || []).includes(student.id)) {
        await updateDoc(classDocRef, {
          enrolledStudents: (classData.enrolledStudents || []).filter((id) => id !== student.id),
        });
      }

      setStudents((prev) => prev.filter((s) => s.id !== student.id));
    } catch (error) {
      console.error("Error removing student from class:", error);
    }
  };

  // Who can hard-delete: the teacher who owns the student (added them, or is in
  // their teachers[]) for the "accidentally added a kid" case, OR a same-school
  // admin cleaning up any student. The security rules enforce the same, so the
  // button only shows when the delete would actually succeed.
  const canDelete = (student) =>
    role === "admin" ||
    (!!uid && (student.addedBy === uid || (student.teachers || []).includes(uid)));

  // Permanently delete the student doc + their sign-out/attendance history.
  // Destructive and irreversible — distinct from "Remove" (detach from class).
  const handleDeletePermanently = async (student) => {
    const name = `${student.firstName || ""} ${student.lastName || ""}`.trim() || "this student";
    const otherClasses = (student.enrolledClasses || []).filter((c) => c !== selectedClass);
    const warning = otherClasses.length
      ? `\n\n⚠️ ${name} is also enrolled in ${otherClasses.length} other class${otherClasses.length > 1 ? "es" : ""} — deleting removes them from the school entirely, not just here. Use "Remove" if you only want them off this class.`
      : "";
    if (!window.confirm(
      `Permanently delete ${name}? This removes the student and their sign-out/attendance history from the school for good. This can't be undone.${warning}`
    )) return;

    try {
      await deleteDoc(doc(db, "students", student.id));

      // Reconcile this class doc's own enrolledStudents array.
      const classDocRef = doc(db, "classes", selectedClass);
      const classDoc = await getDoc(classDocRef);
      const classData = classDoc.data() || {};
      if ((classData.enrolledStudents || []).includes(student.id)) {
        await updateDoc(classDocRef, {
          enrolledStudents: (classData.enrolledStudents || []).filter((id) => id !== student.id),
        });
      }

      setStudents((prev) => prev.filter((s) => s.id !== student.id));
    } catch (error) {
      console.error("Error deleting student:", error);
    }
  };

  const handleSaveEdit = async (studentId, updatedData) => {
    try {
      const studentDocRef = doc(db, "students", studentId);
      await updateDoc(studentDocRef, updatedData);

      setStudents((prev) =>
        prev.map((student) =>
          student.id === studentId ? { ...student, ...updatedData } : student
        )
      );
      setEditingStudent(null);
    } catch (error) {
      console.error("Error updating student:", error);
    }
  };

  return (
    <div className="table-container">
      <h2>Enrolled Students</h2>
      <table className="table admin-hide-on-mobile">
        <thead>
          <tr>
            <th>Last Name</th>
            <th>First Name</th>
            <th>Nickname</th>
            <th>Student ID</th>
            <th>Grade</th>
            <th>School Email</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {students.length > 0 ? (
            students.map((student) =>
              editingStudent?.id === student.id ? (
                <tr key={student.id}>
                  <td data-label="Last Name">
                    <input
                      type="text"
                      defaultValue={student.lastName}
                      onChange={(e) =>
                        setEditingStudent((prev) => ({
                          ...prev,
                          lastName: e.target.value,
                        }))
                      }
                    />
                  </td>
                  <td data-label="First Name">
                    <input
                      type="text"
                      defaultValue={student.firstName}
                      onChange={(e) =>
                        setEditingStudent((prev) => ({
                          ...prev,
                          firstName: e.target.value,
                        }))
                      }
                    />
                  </td>
                  <td data-label="Nickname">
                    <input
                      type="text"
                      defaultValue={student.nickname || ""}
                      placeholder="Goes by…"
                      onChange={(e) =>
                        setEditingStudent((prev) => ({
                          ...prev,
                          nickname: e.target.value,
                        }))
                      }
                    />
                  </td>
                  <td data-label="Student ID">
                    <input
                      type="text"
                      defaultValue={student.studentId}
                      onChange={(e) =>
                        setEditingStudent((prev) => ({
                          ...prev,
                          studentId: e.target.value,
                        }))
                      }
                    />
                  </td>
                  <td data-label="Grade">
                    <input
                      type="text"
                      defaultValue={studentGrade(student)}
                      onChange={(e) =>
                        setEditingStudent((prev) => ({
                          ...prev,
                          grade: e.target.value,
                        }))
                      }
                    />
                  </td>
                  <td data-label="School Email">
                    <input
                      type="email"
                      defaultValue={student.schoolEmail || ""}
                      placeholder="student@school.org"
                      onChange={(e) =>
                        setEditingStudent((prev) => ({
                          ...prev,
                          schoolEmail: e.target.value,
                        }))
                      }
                    />
                  </td>
                  <td data-label="">
                    <button
                      onClick={() =>
                        handleSaveEdit(student.id, {
                          lastName: editingStudent.lastName,
                          firstName: editingStudent.firstName,
                          studentName: `${editingStudent.firstName} ${editingStudent.lastName}`,
                          nickname: (editingStudent.nickname || "").trim(),
                          studentId: editingStudent.studentId,
                          ...gradeFields(editingStudent.grade),
                          schoolEmail: (editingStudent.schoolEmail || "").trim().toLowerCase(),
                        })
                      }
                    >
                      Save
                    </button>
                    <button onClick={() => setEditingStudent(null)}>
                      Cancel
                    </button>
                  </td>
                </tr>
              ) : (
                <tr key={student.id}>
                  <td data-label="Last Name">{student.lastName}</td>
                  <td data-label="First Name">{student.firstName}</td>
                  <td data-label="Nickname">{student.nickname || <span style={{ color: "var(--color-text-muted)" }}>—</span>}</td>
                  <td data-label="Student ID">{student.studentId}</td>
                  <td data-label="Grade">{studentGrade(student)}</td>
                  <td className="as-email-cell" data-label="School Email">
                    {student.schoolEmail || <span style={{ color: "var(--color-text-muted)" }}>—</span>}
                  </td>
                  <td data-label="">
                    <button onClick={() => setEditingStudent({ ...student, grade: studentGrade(student) })}>
                      Edit
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => handleRemove(student)}
                      title="Remove from this class (keeps them in the directory and other classes)"
                    >
                      Remove
                    </button>
                    {canDelete(student) && (
                      <button
                        className="red-button"
                        onClick={() => handleDeletePermanently(student)}
                        title="Permanently delete this student and their history from the school"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              )
            )
          ) : (
            <tr>
              <td colSpan="7" data-label="">No students enrolled</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Phones: compact expandable cards — name collapsed, tap for details + actions. */}
      <MobileTableCards
        items={students}
        getKey={(s) => s.id}
        getTitle={(s) => `${s.firstName || ""} ${s.lastName || ""}`.trim() || "—"}
        getSubtitle={(s) => `${s.nickname ? `“${s.nickname}” · ` : ""}ID ${s.studentId || "—"} · Gr ${studentGrade(s)}`}
        getFields={(s) =>
          editingStudent?.id === s.id
            ? []
            : [
                ["Last name", s.lastName],
                ["First name", s.firstName],
                ["Student ID", s.studentId],
                ["Grade", studentGrade(s)],
                ["School email", s.schoolEmail || "—"],
              ]
        }
        renderExtra={(s) =>
          editingStudent?.id === s.id ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
              {[
                ["Last Name", "lastName", "text"],
                ["First Name", "firstName", "text"],
                ["Nickname", "nickname", "text"],
                ["Student ID", "studentId", "text"],
                ["Grade", "grade", "text"],
                ["School Email", "schoolEmail", "email"],
              ].map(([label, key, type]) => (
                <label key={key} style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 13, fontWeight: 600, color: "var(--color-secondary)" }}>
                  {label}
                  <input
                    type={type}
                    defaultValue={key === "grade" ? studentGrade(s) : (s[key] || "")}
                    onChange={(e) => setEditingStudent((prev) => ({ ...prev, [key]: e.target.value }))}
                    style={{ width: "100%" }}
                  />
                </label>
              ))}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() =>
                    handleSaveEdit(s.id, {
                      lastName: editingStudent.lastName,
                      firstName: editingStudent.firstName,
                      studentName: `${editingStudent.firstName} ${editingStudent.lastName}`,
                      nickname: (editingStudent.nickname || "").trim(),
                      studentId: editingStudent.studentId,
                      ...gradeFields(editingStudent.grade),
                      schoolEmail: (editingStudent.schoolEmail || "").trim().toLowerCase(),
                    })
                  }
                >
                  Save
                </button>
                <button className="btn-secondary" onClick={() => setEditingStudent(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <button onClick={() => setEditingStudent({ ...s, grade: studentGrade(s) })}>Edit</button>
              <button className="btn-secondary" onClick={() => handleRemove(s)}>Remove</button>
              {canDelete(s) && (
                <button className="red-button" onClick={() => handleDeletePermanently(s)}>Delete</button>
              )}
            </>
          )
        }
      />
    </div>
  );
};

export default DisplayEnrolledStudentsTable;
